import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, Filter, RefreshCw, LayoutGrid, List, Rows3, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { CalendarSummaryCards } from './CalendarSummaryCards';

const VIEW_MODES = [
  { key: 'day', label: 'Day', icon: List },
  { key: 'week', label: 'Week', icon: LayoutGrid },
  { key: 'month', label: 'Month', icon: CalendarIcon },
  { key: 'timeline', label: 'Timeline', icon: Rows3 },
];

function PublishStatusPill({ unpublishedCount, lastPublishedAt }) {
  if (unpublishedCount > 0) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 ml-1.5"
        title={`${unpublishedCount} shift${unpublishedCount === 1 ? '' : 's'} not yet published in this view.`}
        data-testid="publish-status-unpublished"
      >
        <AlertCircle className="w-2.5 h-2.5" />
        {unpublishedCount} unpublished
      </span>
    );
  }
  if (lastPublishedAt) {
    let agoLabel = '';
    try { agoLabel = formatDistanceToNow(new Date(lastPublishedAt), { addSuffix: true }); } catch { agoLabel = ''; }
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 ml-1.5"
        title={`Last publish: ${new Date(lastPublishedAt).toLocaleString()}`}
        data-testid="publish-status-published"
      >
        <CheckCircle2 className="w-2.5 h-2.5" />
        Published {agoLabel}
      </span>
    );
  }
  return null;
}

// Top header for Calendar V3: title, view mode tabs, action buttons, summary, date navigation.
export function CalendarToolbar({
  viewMode, setViewMode,
  filteredShifts, dateLabel,
  onPrev, onToday, onNext,
  onCreateShift, onPublish, onToggleFilters, onRefresh,
  unpublishedCount = 0, lastPublishedAt = null,
}) {
  return (
    <div className="flex-shrink-0 border-b bg-white px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Scheduling Board</h1>
          <p className="text-xs text-muted-foreground">Visual shift management & staff scheduling</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-md overflow-hidden">
            {VIEW_MODES.map(v => (
              <button key={v.key} onClick={() => setViewMode(v.key)}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${viewMode === v.key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                data-testid={`view-${v.key}`}>
                <v.icon className="w-3.5 h-3.5" /> {v.label}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={onCreateShift} data-testid="create-shift-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Shift
          </Button>
          <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onPublish} data-testid="publish-schedule-btn">
            <Send className="w-3.5 h-3.5 mr-1" /> Publish
          </Button>
          <PublishStatusPill unpublishedCount={unpublishedCount} lastPublishedAt={lastPublishedAt} />
          <Button variant="outline" size="sm" onClick={onToggleFilters}><Filter className="w-3.5 h-3.5 mr-1" /> Filters</Button>
          <Button variant="outline" size="sm" onClick={onRefresh}><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <CalendarSummaryCards shifts={filteredShifts} />
        <div className="flex items-center gap-1 ml-3">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onPrev}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={onToday}>Today</Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onNext}><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-xs font-medium text-slate-700 ml-1 min-w-[140px]">{dateLabel}</span>
        </div>
      </div>
    </div>
  );
}
